/**
 * `inspector.focusField` plumbing (W2-06; DESIGN.md §8.2 "Double-click: focus the inspector field under the
 * cursor", §5.10 Problems "Go"): the command asks the mounted inspector panel(s) and the Project settings dialog to
 * focus the control of a field, identified by the field path every inspector field carries as `data-help-id`
 * ('note.x', 'line.name', 'segment.ease'...) or, where the control has no help id of its own, as
 * `data-focus-field` on a wrapper.
 *
 *  - Aliases: the motion columns name the arrive / leave halves of a jump keyframe 'key.vIn' / 'key.vOut'; they
 *    resolve to the arrive / leave fields, else to the single Value field.
 *  - Collapsed sections ("More", "Advanced") that hold the field are expanded first; the caller retries once they
 *    rendered (a hidden control cannot take focus in a browser).
 *  - A field whose own control is disabled (an animated note tilt) focuses the first action of its row ("Make
 *    static"); a read-only field (RPE-only values) is focused itself so it scrolls into view.
 *  - Chart-level fields (`pkg.*`, `meta.preview*`) live in the Project settings dialog: `settingsSectionOf` maps
 *    them to its sections, and a pending field tells the dialog which control to focus when it opens.
 *  - The song, illustration and video files (`meta.music`, `meta.illustration`, `meta.unlockVideo`, `asset.*`)
 *    are edited in the Assets panel; the rest of the package metadata (`meta.name`, `meta.tags`, `meta.charter`,
 *    `meta.composer`, `meta.level`...) in the Publish workspace's PACKAGE panel (DESIGN.md §4.16). The inspector
 *    shows none of those: the command routes them there (`isAssetField`, `isPublishField`) instead of looking for
 *    them in the inspector.
 */

import type { Unsubscribe } from '../../core/util/types';

type Handler = (field: string) => boolean;

const handlers = new Set<Handler>();

/** Registers a panel or dialog; the handler returns true when it focused (or will focus) the field. */
export function onFocusFieldRequest(h: Handler): Unsubscribe {
  handlers.add(h);
  return () => {
    handlers.delete(h);
  };
}

/** Asks every mounted panel; true when one focused the field. */
export function requestFocusField(field: string): boolean {
  for (const h of handlers) if (h(field)) return true;
  return false;
}

// ---------------------------------------------------------------------------------------------------------------
// Field names
// ---------------------------------------------------------------------------------------------------------------

/** Other names of a field, most specific first (motion column halves of a jump keyframe). */
const ALIASES: Readonly<Record<string, readonly string[]>> = {
  // Arrive half: the arrive field; a plain key has only Value; an arrive fixed by a Stay falls to the leave field.
  'key.vIn': ['key.value.in', 'key.value', 'key.value.out'],
  'key.vOut': ['key.value.out', 'key.value'],
};

/** The field paths tried for `field`, in order. */
export function fieldCandidates(field: string): readonly string[] {
  return ALIASES[field] ?? [field];
}

/** Sections of the Project settings dialog ('preview' is internal: the contract has no id for it, CR-W2-06-2). */
export type SettingsSectionId = 'display' | 'gameplay' | 'speed' | 'preview' | 'export';

const SETTINGS_SECTION: Readonly<Record<string, SettingsSectionId>> = {
  'pkg.aspectRatio': 'display',
  'pkg.aspectPreset': 'display',
  'pkg.forceAspectRatio': 'display',
  'pkg.backgroundDim': 'display',
  'pkg.holdPartialCover': 'gameplay',
  'pkg.noteUniformScale': 'gameplay',
  'pkg.useAttachUiFix': 'gameplay',
  'pkg.lineLength': 'gameplay',
  'pkg.speedMode': 'speed',
  'pkg.rpeVersion': 'speed',
  'pkg.speedBakeTolPx': 'speed',
  'pkg.exportProfile': 'export',
  'pkg.fileNaming': 'export',
  'pkg.infoOffsetSec': 'export',
};

/** Project settings fields shown at the Full level only (the dialog reveals them when one is asked for). */
export const FULL_ONLY_SETTINGS: ReadonlySet<string> = new Set(['pkg.rpeVersion', 'pkg.speedBakeTolPx']);

/** Whether a field lives in the Project settings dialog (package settings and the song-select preview). */
export function isSettingsField(field: string): boolean {
  return field.startsWith('pkg.') || field.startsWith('meta.preview');
}

/** The Project settings section of a field; null when it has none (unknown package fields open the dialog top). */
export function settingsSectionOf(field: string): SettingsSectionId | null {
  const known = SETTINGS_SECTION[field];
  if (known !== undefined) return known;
  return field.startsWith('meta.preview') ? 'preview' : null;
}

/** Fields edited in the Assets panel: the project files and the chart's music, illustration and video. */
export function isAssetField(field: string): boolean {
  return (
    field.startsWith('asset.') ||
    field === 'meta.music' ||
    field === 'meta.illustration' ||
    field === 'meta.unlockVideo'
  );
}

/**
 * Package metadata edited in the Publish workspace (title, artists, charter, level and difficulty, tags,
 * description, tip...): every `meta.*` field that is neither a Project settings field (the song-select preview)
 * nor a file of the Assets panel.
 */
export function isPublishField(field: string): boolean {
  return field.startsWith('meta.') && !isSettingsField(field) && !isAssetField(field);
}

// ---------------------------------------------------------------------------------------------------------------
// Pending Project settings field
// ---------------------------------------------------------------------------------------------------------------

/** A request older than this is stale (the dialog failed to open): it never focuses a later, unrelated opening. */
const PENDING_MS = 3000;

let pending: { readonly field: string; readonly at: number } | null = null;

/** The command is about to open Project settings for `field`. */
export function setPendingSettingsField(field: string): void {
  pending = { field, at: Date.now() };
}

/** The field the opening Project settings dialog should focus (read during render: no side effect). */
export function peekPendingSettingsField(): string | null {
  if (pending === null || Date.now() - pending.at > PENDING_MS) return null;
  return pending.field;
}

/** The dialog took the pending field. */
export function clearPendingSettingsField(): void {
  pending = null;
}

// ---------------------------------------------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------------------------------------------

const ENABLED = ':not([disabled]):not([aria-disabled="true"])';

/** Value controls (what the user edits). */
const CONTROLS = [
  `input${ENABLED}:not([type="hidden"]):not([aria-hidden="true"])`,
  `textarea${ENABLED}`,
  `select${ENABLED}`,
  ...['combobox', 'checkbox', 'radio', 'switch', 'slider', 'spinbutton', 'textbox'].map(
    (r) => `[role="${r}"]${ENABLED}`,
  ),
].join(', ');

/** The selected option of a radio group (focus lands on the current choice). */
const CHECKED = `input[type="radio"]:checked${ENABLED}, [role="radio"][aria-checked="true"]${ENABLED}`;

/** Other focusable elements: buttons, links, custom widgets. */
const FOCUSABLE = `button${ENABLED}, a[href], [tabindex]:not([tabindex="-1"])`;

/** Info popover triggers and problem badges are never the field itself. */
function isAside(el: Element): boolean {
  return el.matches('[aria-haspopup="dialog"], [data-kind]');
}

function attrValue(field: string): string {
  return field.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
 * The elements standing for `field` inside `root`: explicit focus targets first, then help ids, and among those the
 * field rows before other mentions (the line property list names every property with the same help id).
 */
function hostsOf(root: HTMLElement, field: string): HTMLElement[] {
  const v = attrValue(field);
  const all = [
    ...root.querySelectorAll<HTMLElement>(`[data-focus-field="${v}"]`),
    ...root.querySelectorAll<HTMLElement>(`[data-help-id="${v}"]`),
  ];
  const inRow = all.filter((el) => el.closest('[data-field-row]') !== null);
  return [...new Set([...inRow, ...all])];
}

/** The control of a host: the host itself, the checked radio, the first value control. */
function controlIn(host: HTMLElement): HTMLElement | null {
  if (host.matches(CONTROLS)) return host;
  return host.querySelector<HTMLElement>(CHECKED) ?? host.querySelector<HTMLElement>(CONTROLS);
}

/** A field whose own control is unusable: another control or action of its row. */
function actionNear(host: HTMLElement): HTMLElement | null {
  const row = host.closest<HTMLElement>('[data-field-row]') ?? host;
  const control = controlIn(row);
  if (control !== null) return control;
  for (const el of row.querySelectorAll<HTMLElement>(FOCUSABLE)) if (!isAside(el)) return el;
  return null;
}

interface Target {
  readonly el: HTMLElement;
  /** The element is not an input of its own (a read-only value), focused programmatically. */
  readonly readOnly: boolean;
}

function targetIn(root: HTMLElement, field: string): Target | null {
  const hosts = fieldCandidates(field).flatMap((f) => hostsOf(root, f));
  for (const host of hosts) {
    const el = controlIn(host);
    if (el !== null) return { el, readOnly: false };
  }
  const first = hosts[0];
  if (first === undefined) return null;
  const near = actionNear(first);
  return near === null ? { el: first, readOnly: true } : { el: near, readOnly: false };
}

/**
 * Opens the collapsed sections around `el` (their disclosure buttons, `aria-controls` -> the hidden body); true
 * when one was opened, so the focus has to wait for the next render.
 */
function expandAround(root: HTMLElement, el: HTMLElement): boolean {
  let opened = false;
  for (let p = el.parentElement; p !== null && root.contains(p); p = p.parentElement) {
    if (!p.hidden || p.id === '') continue;
    const toggle = root.querySelector<HTMLElement>(`[aria-controls="${attrValue(p.id)}"][aria-expanded="false"]`);
    if (toggle === null) continue;
    toggle.click();
    opened = true;
  }
  return opened;
}

/**
 * Focuses (and selects, for text inputs) the control of the field inside `root`. False when the field is not
 * there or a collapsed section around it was just opened (the caller retries a frame later).
 */
export function focusFieldIn(root: HTMLElement, field: string): boolean {
  const target = targetIn(root, field);
  if (target === null) return false;
  if (expandAround(root, target.el)) return false;
  const el = target.el;
  if (target.readOnly && !el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
  if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  el.focus();
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) el.select();
  return document.activeElement === el;
}
